/**
 * Common interface for all AI players in the Zodiac Chess battle system
 */

import { GameState, IMove, Position } from "../../types";

/**
 * Result of an AI's move selection
 */
export interface AIMoveResult {
  fromPosition: Position;
  move: IMove;
  confidence: number; // 0-1 score indicating AI's confidence in this move
  thinkingTime: number; // Actual time spent thinking in ms
  evaluation?: number; // Optional position evaluation score
  reasoning?: string; // Optional explanation of why this move was chosen
  evaluationBreakdown?: any; // Optional detailed evaluation breakdown (AI-specific)
  movingPiece?: { type: string; isKing: boolean }; // Optional piece information for display
  principalVariation?: any[]; // Optional best line calculated (for minimax)
  initialBoardValue?: number; // Optional initial board absolute value (Light - Dark) before first PV move
  decisionLog?: string[]; // Optional priority decision chain trace (for priority models)
  rootMoveScores?: Array<{ description: string; score: number }>; // Optional root move scores from minimax (for diagnostics)
}

/**
 * Statistics about AI performance
 */
export interface AIStats {
  movesCalculated: number;
  averageThinkingTime: number;
  peakMemoryUsage?: number;
  nodesEvaluated?: number;
}

/**
 * Common interface that all AI players must implement
 */
export interface IAIPlayer {
  /**
   * Unique identifier for this AI
   */
  readonly name: string;

  /**
   * Display name for UI
   */
  readonly displayName: string;

  /**
   * Description of the AI's approach
   */
  readonly description: string;

  /**
   * Select a king piece for game start
   * @param availableKings Array of piece types that can be selected as king
   * @param isLightSide Whether selecting for light or dark side
   * @returns The selected piece type to use as king
   */
  selectKing(availableKings: import("../../types").PieceType[], isLightSide: boolean): import("../../types").PieceType;

  /**
   * Optional: Select a piece during drafting phase
   * @param availablePieces Array of piece types that can be selected
   * @param opponentPieces Array of pieces already selected by opponent
   * @param ownPieces Array of pieces already selected by this player
   * @returns The selected piece type
   */
  selectPiece?(availablePieces: import("../../types").PieceType[], opponentPieces: import("../../types").PieceType[], ownPieces: import("../../types").PieceType[]): import("../../types").PieceType;

  /**
   * Select the best move for the current game state
   * @param gameState Current game state
   * @param maxThinkingTime Maximum time allowed in milliseconds (typically 10000ms)
   * @returns The selected move and metadata
   */
  selectMove(gameState: GameState, maxThinkingTime: number): Promise<AIMoveResult>;

  /**
   * Get statistics about the AI's performance
   */
  getStats(): AIStats;

  /**
   * Reset the AI's internal state (called between games)
   */
  reset(): void;

  /**
   * Optional: Configure AI parameters
   */
  configure?(config: Record<string, any>): void;
}

/**
 * AI Player types available in the system
 */
export enum AIPlayerType {
  PLAYER = "player",
  DIONS_AI = "dions",
  MINIMAX = "minimax",
  MCTS = "mcts",
  ULTIMATE = "ultimate",
  WISP = "wisp",
  CREATURE = "creature",
  ZSTR = "zstr",
  ZSTR_BLIND = "zstr-blind",
}

/**
 * Configuration for AI vs AI battles
 */
export interface BattleConfig {
  lightPlayer: AIPlayerType;
  darkPlayer: AIPlayerType;
  minMoveDelay: number; // Minimum delay between moves (1000ms)
  maxThinkingTime: number; // Maximum thinking time per move (10000ms)
  enableAnalysis: boolean; // Whether to collect detailed analysis
}
